'use client';

import { BasicForm } from '@/components/BasicForm';
import { DYNAMIC_LOAD_TIMEOUT } from '@/constants';
import useDeviceDetect from '@/hooks/deviceDetect';
import { IconArrowDown } from '@/icons/IconArrowDown';
import { IconFilter } from '@/icons/IconFilter';
import useHeaderStore from '@/store/header';
import { getContainerClassName, getInitialPrice, getKeys, getValues, numberFormat } from '@/utils/helper';
import { RangeSlider } from '@mantine/core';
import { useRouter, useParams, usePathname } from 'next/navigation';
import { useSearchParams } from '@/hooks/useCustomSearchParams';
import { useEffect, useRef, useState } from 'react';
import { StyleCheckbox } from '@/components/StyleCheckbox';
import { SizeCheckbox } from '@/components/SizeCheckbox';
import { ColorCheckbox } from '@/components/ColorCheckbox';
import { BasicCheckbox } from '@/components/BasicCheckbox';
import { LocationSelector } from './LocationSelector';
import { useDeliveryOption } from '@/hooks/useDeliveryOptions';
import LocationModal from './LocationModal';
import { IStoreLists } from '@/interfaces/store';
import { GiftVoucher } from '@/enums';

interface Filter {
  heading: string;
  options: any[];
}

const FilterSection = ({
  asContent,
  filter,
  render = (option: any, index: number) => <div>{option.Value}</div>,
  optionsWrapperClassName = '',
  className = '',
  setShowMore,
  showMore,
  totalItems,
  lessItems,
}: {
  asContent: boolean;
  filter: Filter;
  render?: (option: any, index: number) => React.ReactNode;
  optionsWrapperClassName?: string;
  className?: string;
  setShowMore?: any;
  showMore?: boolean;
  totalItems?: number;
  lessItems?: number;
}) => {
  const [open, setOpen] = useState<boolean>(true);
  const optionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMore && optionsRef.current) {
      optionsRef.current.scrollTop = 0;
    }
  }, [showMore]);

  return (
    <section className={`mb-6 pt-5 ${asContent ? '' : 'pr-5'} ${className} ${open ? 'open' : ''}`}>
      <button
        type="button"
        className="mb-5 flex w-full cursor-pointer items-center justify-between bg-transparent p-0 text-xl font-medium"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={open ? 'Collapse filter' : 'Expand filter'}
      >
        <div className="flex w-full justify-between">
          {filter?.heading}
          <IconArrowDown className={`mr-1 w-5 transition duration-300 ${open ? '' : 'rotate-180'}`} aria-hidden="true" />
        </div>
      </button>
      <div
        ref={optionsRef}
        className={`options grid gap-2 overflow-hidden ${optionsWrapperClassName} ${open ? '' : 'hidden'} pr-1 ${
          showMore ? 'overflow-y-scroll' : ''
        }`}
      >
        {filter?.options?.map((option, index) => render(option, index))}
      </div>
      {totalItems && lessItems && totalItems > lessItems && open ? (
        <div className="flex w-full justify-center pt-2">
          <button
            type="button"
            className="w-24 cursor-pointer whitespace-nowrap border-none bg-transparent p-0 text-lg underline hover:opacity-70 focus:outline-2 focus:outline-offset-2 focus:outline-blue-500"
            onClick={() => setShowMore(!showMore)}
            aria-expanded={showMore}
            aria-label={showMore ? 'Show less options' : 'Show more options'}
          >
            {showMore ? 'Show less' : 'Show more'}
          </button>
        </div>
      ) : null}
      <div className={`${filter?.options?.length === 0 && open ? 'block text-gray-500' : 'hidden'}`}>No {filter?.heading} filter available</div>
    </section>
  );
};

const SortBySection = ({ selectedSort, setSelectedSort }: { selectedSort: string; setSelectedSort: any }) => {
  return (
    <>
      <ul className="space-y-1 p-0 text-black">
        <li>
          <div className="flex rounded pb-2">
            <div className="flex h-6 items-center">
              <input
                className="h-5 w-5"
                type="radio"
                id="new_arrivals"
                name="sort_by"
                value="new_arrivals"
                onChange={() => setSelectedSort('new_arrivals')}
                checked={selectedSort == 'new_arrivals'}
              />
            </div>
            <div className="ml-2">
              <label htmlFor="new_arrivals">New Arrivals</label>
            </div>
          </div>
        </li>
        <li>
          <div className="flex rounded pb-2">
            <div className="flex h-6 items-center">
              <input
                className="h-5 w-5"
                type="radio"
                id="best_sellers"
                name="sort_by"
                value="best_sellers"
                onChange={() => setSelectedSort('best_sellers')}
              />
            </div>
            <div className="ml-2">
              <label htmlFor="best_sellers">Best Sellers</label>
            </div>
          </div>
        </li>
        <li>
          <div className="flex rounded pb-2">
            <div className="flex h-6 items-center">
              <input
                className="h-5 w-5"
                type="radio"
                id="trending_now"
                name="sort_by"
                value="trending_now"
                onChange={() => setSelectedSort('trending_now')}
              />
            </div>
            <div className="ml-2">
              <label htmlFor="trending_now">Trending now</label>
            </div>
          </div>
        </li>
        <li>
          <div className="flex rounded pb-2">
            <div className="flex h-6 items-center">
              <input
                className="h-5 w-5"
                type="radio"
                id="price_asc"
                name="sort_by"
                value="price_asc"
                onChange={() => setSelectedSort('price_asc')}
                checked={selectedSort == 'price_asc'}
              />
            </div>
            <div className="ml-2">
              <label htmlFor="price_asc">Price: Low to High</label>
            </div>
          </div>
        </li>
        <li>
          <div className="flex rounded pb-2">
            <div className="flex h-6 items-center">
              <input
                className="h-5 w-5"
                type="radio"
                id="price_desc"
                name="sort_by"
                value="price_desc"
                onChange={() => setSelectedSort('price_desc')}
                checked={selectedSort == 'price_desc'}
              />
            </div>
            <div className="ml-2">
              <label htmlFor="price_desc">Price: High to Low</label>
            </div>
          </div>
        </li>
      </ul>
      <style jsx>{`
        label {
          font-size: 14px;
        }
      `}</style>
    </>
  );
};

const FilterPanel = ({ className = '' }: { className?: string }) => {
  const filtersAvailable = useHeaderStore((state) => state.filtersAvailable);
  const filterPanel = useHeaderStore((state) => state.filterPanel);
  const openFilterPanel = useHeaderStore((state) => state.openFilterPanel);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  useEffect(() => {
    setTimeout(() => {
      setIsMounted(true);
    }, DYNAMIC_LOAD_TIMEOUT);
  }, []);

  const { isMobile } = useDeviceDetect();

  return filtersAvailable ? (
    <>
      <div
        className={`filter-panel absolute bottom-0 right-0 top-0 z-20 flex w-[400px] max-w-full flex-col bg-white transition-all 2xs:translate-y-0 md:top-0 ${className} ${
          filterPanel && isMounted ? 'translate-y-0 2xs:translate-x-0' : 'translate-y-full 2xs:translate-x-full'
        } ${isMobile ? 'top-[var(--header-height)]' : ''}`}
      >
        <header className="mb-8 flex items-center px-7 pt-6">
          <span className="text-2xl">Filter</span>
          <button
            type="button"
            className="a ml-auto flex cursor-pointer border-none bg-transparent p-0 text-xl hover:opacity-70 focus:outline-2 focus:outline-offset-2 focus:outline-blue-500"
            onClick={() => openFilterPanel(false)}
            aria-label="Close filter panel"
          >
            <IconFilter className="w-8" aria-hidden="true" />
          </button>
        </header>
        <FilterContent className={className} />
      </div>

      <style jsx global>{`
        .filter-form label.checkbox,
        .filter-form label.radio {
          font-size: 14px;
        }

        .filter-form:before,
        .filter-form:after {
          content: '';
          position: absolute;
          width: 100%;
          height: 1.25rem;
          display: block;
          background: rgb(255, 255, 255);
          z-index: 1;
        }

        .filter-form:before {
          background: linear-gradient(
            0deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.4) 20%,
            rgba(255, 255, 255, 0.6) 40%,
            rgba(255, 255, 255, 0.8) 60%,
            rgba(255, 255, 255, 1) 80%,
            rgba(255, 255, 255, 1) 100%
          );
          top: 0;
        }
        .filter-form:after {
          background: linear-gradient();
          @media only screen and (max-width: 768px) {
            bottom: 6rem;
          }
        }
      `}</style>
    </>
  ) : (
    <></>
  );
};

export const FilterContent = ({
  className,
  asContent = false,
  initialFilterData,
  priceRangeData,
  isSearchPage = false,
}: {
  className: string;
  asContent?: boolean;
  initialFilterData?: [
    {
      Criteria: string;
      Values: [
        {
          Count: number;
          Value: string;
        },
      ];
    },
  ];
  priceRangeData?: {
    Min: number;
    Max: number;
  };

  isSearchPage?: boolean;
}) => {
  const setFilterData = useHeaderStore((state) => state.setFilterData);
  const filterAttribute = useHeaderStore((state) => state.filterAttribute);
  const priceRange = useHeaderStore((state) => state.priceRange);
  const filterLoading = useHeaderStore((state) => state.filterLoading);
  const openFilterPanel = useHeaderStore((state) => state.openFilterPanel);
  const styleImageMapping = useHeaderStore((state) => state.styleImageMapping);
  const styleSVGSourceCode = useHeaderStore((state) => state.styleSVGSourceCode);
  const setSortByState = useHeaderStore((state) => state.setSortByState);
  const setClearPageIndex = useHeaderStore((state) => state.setClearPageIndex);
  const colorDictionary = useHeaderStore((state) => state.colorDictionary);
  const showPickInStoreOption = useHeaderStore((state) => state.showPickInStoreOption);
  const showExpressOption = useHeaderStore((state) => state.showExpressOption);
  const setIsExpressEnabled = useHeaderStore((state) => state.setIsExpressEnabled);

  const { isTablet } = useDeviceDetect();
  const router = useRouter();
  const searchParams = useSearchParams();
  const params = useParams();
  const pathname = usePathname();

  // Extract path and query parameters
  const key = params?.key;
  const submodule = params?.submodule;
  const module = params?.module;
  const filters = searchParams?.get('filters');
  const pageIndex = searchParams?.get('pageIndex');
  const sortBy = searchParams?.get('sortby');

  const [giftVoucher, setGiftVoucher] = useState(() => {
    if (key) {
      return !!(key.includes(GiftVoucher.gift) || key.includes(GiftVoucher.voucher));
    } else if (submodule) {
      return !!(submodule.includes(GiftVoucher.gift) || submodule.includes(GiftVoucher.voucher));
    }
    return false;
  });
  const [pageType, setPageType] = useState(module);
  const [selectedSort, setSelectedSort] = useState('');
  const [priceRangeOpen, setPriceRangeOpen] = useState<boolean>(true);
  const [sortByOpen, setSortByOpen] = useState<boolean>(true);
  const [price, setPrice] = useState(() => getInitialPrice(filters, priceRange));
  const [clearPriceFilter, setClearPriceFilter] = useState<boolean>(false);
  const [isActiveClearPriceFilter, setIsActiveClearPriceFilter] = useState<boolean>(false);
  const [genderFilter, setGenderFilter] = useState(
    (() => {
      let returnVal: { heading: string; options: string[] } = {} as { heading: string; options: string[] };
      initialFilterData?.map((filter: any, index: number) => {
        if (filter?.Criteria === 'gender') {
          returnVal = {
            heading: 'Gender',
            options: filter.Values,
          };
        }
      });
      return returnVal;
    })(),
  );
  const [sizeFilter, setSizeFilter] = useState(
    (() => {
      let returnVal: { heading: string; options: string[] } = {} as { heading: string; options: string[] };
      initialFilterData?.map((filter: any, index: number) => {
        if (filter?.Criteria === 'sizes') {
          returnVal = {
            heading: 'Size',
            options: filter.Values,
          };
        }
      });
      return returnVal;
    })(),
  );
  const [colorFilter, setColorFilter] = useState(
    (() => {
      let returnVal: { heading: string; options: string[] } = {} as { heading: string; options: string[] };
      initialFilterData?.map((filter: any, index: number) => {
        if (filter?.Criteria === 'productColor') {
          returnVal = {
            heading: 'Color',
            options: filter.Values,
          };
        }
      });
      return returnVal;
    })(),
  );
  const [brandFilter, setBrandFilter] = useState(
    (() => {
      let returnVal: { heading: string; options: string[] } = {} as { heading: string; options: string[] };
      initialFilterData?.map((filter: any, index: number) => {
        if (filter?.Criteria === 'brandName') {
          returnVal = {
            heading: 'Brand',
            options: filter.Values,
          };
        }
      });
      return returnVal;
    })(),
  );
  const [categoryFilter, setCategoryFilter] = useState(
    (() => {
      let returnVal: { heading: string; options: string[] } = {} as { heading: string; options: string[] };
      initialFilterData?.map((filter: any, index: number) => {
        if (filter?.Criteria === 'categories') {
          returnVal = {
            heading: 'Category',
            options: filter.Values,
          };
        }
      });
      return returnVal;
    })(),
  );
  const [styleFilter, setStyleFilter] = useState(
    (() => {
      let returnVal: { heading: string; options: string[] } = {} as { heading: string; options: string[] };
      initialFilterData?.map((filter: any, index: number) => {
        if (filter?.Criteria === 'styles') {
          returnVal = {
            heading: 'Style',
            options: filter.Values,
          };
        }
      });
      return returnVal;
    })(),
  );
  const [lessStyleFilter, setLessStyleFilter] = useState(
    (() => {
      let returnVal: { heading: string; options: string[] } = {} as { heading: string; options: string[] };
      initialFilterData?.map((filter: any, index: number) => {
        if (filter?.Criteria === 'styles') {
          returnVal = {
            heading: 'Style',
            options: filter.Values?.slice(0, 4),
          };
        }
      });
      return returnVal;
    })(),
  );
  const [lessColorFilter, setLessColorFilter] = useState(
    (() => {
      let returnVal: { heading: string; options: string[] } = {} as { heading: string; options: string[] };
      initialFilterData?.map((filter: any, index: number) => {
        if (filter?.Criteria === 'productColor') {
          returnVal = {
            heading: 'Color',
            options: filter.Values?.slice(0, 6),
          };
        }
      });
      return returnVal;
    })(),
  );
  const [lessSizeFilter, setLessSizeFilter] = useState(
    (() => {
      let returnVal: { heading: string; options: string[] } = {} as { heading: string; options: string[] };
      initialFilterData?.map((filter: any, index: number) => {
        if (filter?.Criteria === 'sizes') {
          returnVal = {
            heading: 'Size',
            options: isTablet ? filter.Values?.slice(0, 9) : filter.Values?.slice(0, 12),
          };
        }
      });
      return returnVal;
    })(),
  );
  const [lessCategoryFilter, setLessCategoryFilter] = useState(
    (() => {
      let returnVal: { heading: string; options: string[] } = {} as { heading: string; options: string[] };
      initialFilterData?.map((filter: any, index: number) => {
        if (filter?.Criteria === 'categories') {
          returnVal = {
            heading: 'Category',
            options: filter.Values?.slice(0, 5),
          };
        }
      });
      return returnVal;
    })(),
  );
  const [lessBrandFilter, setLessBrandFilter] = useState(
    (() => {
      let returnVal: { heading: string; options: string[] } = {} as { heading: string; options: string[] };
      initialFilterData?.map((filter: any, index: number) => {
        if (filter?.Criteria === 'brandName') {
          returnVal = {
            heading: 'Brand',
            options: filter.Values?.slice(0, 5),
          };
        }
      });
      return returnVal;
    })(),
  );
  const [lessGenderFilter, setLessGenderFilter] = useState(
    (() => {
      let returnVal: { heading: string; options: string[] } = {} as { heading: string; options: string[] };
      initialFilterData?.map((filter: any, index: number) => {
        if (filter?.Criteria === 'gender') {
          returnVal = {
            heading: 'Gender',
            options: filter.Values?.slice(0, 5),
          };
        }
      });
      return returnVal;
    })(),
  );
  const [showMoreStyle, setShowMoreStyle] = useState<boolean>(false);
  const [showMoreColor, setShowMoreColor] = useState<boolean>(false);
  const [showMoreSize, setShowMoreSize] = useState<boolean>(false);
  const [showMoreCategory, setShowMoreCategory] = useState<boolean>(false);
  const [showMoreBrand, setShowMoreBrand] = useState<boolean>(false);
  const [showMoreGender, setShowMoreGender] = useState<boolean>(false);
  const [selectedGender, setSelectedGender] = useState([]);
  const [selectedSize, setSelectedSize] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [selectedColor, setSelectedColor] = useState([]);
  const [selectedStyle, setSelectedStyle] = useState([]);
  const [styleImage, setStyleImage] = useState<string[]>([]);
  const [colorCode, setColorCode] = useState<string[]>([]);
  const [showLocationModal, setShowLocationModal] = useState<boolean>(false);
  const [isMyLocation, setIsMyLocation] = useState<boolean>(false);
  const isStoreSelected = useHeaderStore((state) => state.isStoreSelected);
  const setSelectStore = useHeaderStore((state) => state.setSelectedStore);
  const { myLocation, selectedStore, isExpressEnabled } = useDeliveryOption();
  const setIsStoreSelected = useHeaderStore((state) => state.setIsStoreSelected);
  const [isBopis, setIsBopis] = useState<boolean>(false);
  const setSelectedStore = useHeaderStore((state) => state.setSelectedStore);

  const styleKeys: string[] = getKeys(styleImageMapping);
  const styleValues: string[] = getValues(styleImageMapping);
  const colorKeys: string[] = getKeys(colorDictionary);
  const colorValues: string[] = getValues(colorDictionary);

  useEffect(() => {
    // Wait for searchParams to load
    if (!searchParams) return;

    if (filters) {
      const getMinPrice = () => {
        const minPriceFromQuery = filtersFromQuery?.price?.[0]?.split(' ')[0];
        if (minPriceFromQuery) {
          return +minPriceFromQuery;
        }
        return Math.floor(priceRangeData ? priceRangeData.Min : priceRange.Min);
      };

      const getMaxPrice = () => {
        const maxPriceFromQuery = filtersFromQuery?.price?.[0]?.split(' ')[2];
        if (maxPriceFromQuery) {
          return +maxPriceFromQuery;
        }
        return Math.ceil(priceRangeData ? priceRangeData.Max : priceRange.Max);
      };
      let filtersFromQuery = JSON.parse(filters.toString());

      setSelectedGender(filtersFromQuery.gender);
      setSelectedBrand(filtersFromQuery.brandSeName);
      let tempPrice = {
        minPrice: getMinPrice(),
        maxPrice: getMaxPrice(),
      };
      setPrice(tempPrice);
      setSelectedColor(filtersFromQuery.productColor);
      setSelectedSize(filtersFromQuery.sizes);
      setSelectedStyle(filtersFromQuery.styles);
      setSelectedCategory(filtersFromQuery.categories);
      setFilterData(filters);

      if (filtersFromQuery?.price?.[0]?.split(' ')[0] && filtersFromQuery.price?.[0]?.split(' ')[2]) {
        setIsActiveClearPriceFilter(true);
      }
    } else {
      setSelectedGender([]);
      setSelectedBrand([]);
      setSelectedColor([]);
      setSelectedSize([]);
      setSelectedStyle([]);
      // Only set price if priceRange has valid values
      // Otherwise, the useEffect below will handle it when priceRange loads
      if (priceRange.Min > 0 && priceRange.Max > 0) {
        setPrice({ minPrice: Math.floor(priceRange.Min), maxPrice: Math.ceil(priceRange.Max) });
      }
      setIsActiveClearPriceFilter(false);
      setSelectedCategory([]);
      setFilterData('');
    }
    const typedSortBy = sortBy as string;
    if (typedSortBy) {
      setSortByState(typedSortBy);
      setSelectedSort(typedSortBy);
    } else if (!isSearchPage) {
      setSortByState('new_arrivals');
    }
  }, [filters, pageIndex, submodule, module, sortBy, searchParams]);

  useEffect(() => {
    if (key) {
      setGiftVoucher(!!(key.includes(GiftVoucher.gift) || key.includes(GiftVoucher.voucher)));
    } else if (submodule) {
      setGiftVoucher(!!(submodule.includes(GiftVoucher.gift) || submodule.includes(GiftVoucher.voucher)));
    }
    setPageType(module);
    return () => {
      setSelectedGender([]);
      setSelectedBrand([]);
      setSelectedColor([]);
      setSelectedSize([]);
      setSelectedCategory([]);
      setSelectedStyle([]);
    };
  }, [key, submodule, module]);

  // Update price when priceRange loads and there are no filters
  useEffect(() => {
    if (!filters && priceRange.Min > 0 && priceRange.Max > 0) {
      setPrice({ minPrice: Math.floor(priceRange.Min), maxPrice: Math.ceil(priceRange.Max) });
    }
  }, [priceRange.Min, priceRange.Max, filters]);

  useEffect(() => {
    if (filterAttribute?.length > 0) {
      let colorOption: any = [];
      let colorCode: string[] = [];
      let optionValues: any = [];
      let optionImages: string[] = [];
      filterAttribute?.map((filter: any, index: number) => {
        switch (filter?.Criteria) {
          case 'brandSeName':
            setBrandFilter({ heading: 'Brand', options: filter?.Values });
            break;
          case 'productColor':
            filter?.Values?.forEach((value: any) => {
              colorValues?.forEach((color, index: number) => {
                if (value?.Value.trim() === color.trim()) {
                  colorOption.push(value);
                  let code = colorKeys[index].toString();
                  colorCode.push(code.substring(4));
                }
              });
            });
            setColorCode(colorCode);
            setColorFilter({ heading: 'Color', options: colorOption });

            break;
          case 'sizes':
            setSizeFilter({ heading: 'Size', options: filter?.Values });
            break;
          case 'gender':
            setGenderFilter({ heading: 'Gender', options: filter?.Values });
            break;
          case 'styles':
            filter?.Values?.forEach((value: any) => {
              styleValues?.forEach((style, index: number) => {
                if (value?.Value.trim() === style.trim()) {
                  optionValues.push(value);
                  optionImages.push(styleKeys[index]);
                }
              });
            });

            setStyleImage(optionImages);
            setStyleFilter({ heading: 'Style', options: optionValues });
            break;
          case 'categories':
            setCategoryFilter({ heading: 'Category', options: filter?.Values });
            break;
          default:
            break;
        }
      });
    } else {
      setStyleFilter({ heading: 'Style', options: [] });
      setCategoryFilter({ heading: 'Category', options: [] });
      setGenderFilter({ heading: 'Gender', options: [] });
      setColorFilter({ heading: 'Color', options: [] });
      setBrandFilter({ heading: 'Brand', options: [] });
      setSizeFilter({ heading: 'Size', options: [] });
    }
  }, [filterAttribute]);

  useEffect(() => {
    // We no longer reset showMore states here to persist UI preference across data updates
    setLessStyleFilter({ heading: 'Style', options: styleFilter.options?.slice(0, 4) });
    setLessColorFilter({ heading: 'Color', options: colorFilter.options?.slice(0, 6) });
    setLessSizeFilter({ heading: 'Size', options: isTablet ? sizeFilter.options?.slice(0, 9) : sizeFilter.options?.slice(0, 12) });
    setLessCategoryFilter({ heading: 'Category', options: categoryFilter.options?.slice(0, 5) });
    setLessBrandFilter({ heading: 'Brand', options: brandFilter.options?.slice(0, 5) });
    setLessGenderFilter({ heading: 'Gender', options: genderFilter.options?.slice(0, 5) });
  }, [styleFilter, colorFilter, sizeFilter, categoryFilter, brandFilter, genderFilter]);

  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = (formData: any) => {
    window.scrollTo(0, 0);
    setSortByState(selectedSort);
    delete formData['sort_by'];

    if (
      formData.hasOwnProperty('price_from') &&
      formData.hasOwnProperty('price_to') &&
      (+formData['price_to'] !== priceRange.Max || +formData['price_from'] !== priceRange.Min)
    ) {
      let min = formData['price_from'];
      let max = formData['price_to'];
      delete formData['price_from'];
      delete formData['price_to'];

      if (clearPriceFilter === false || (!clearPriceFilter && isActiveClearPriceFilter)) {
        setTimeout(() => {
          setIsActiveClearPriceFilter(true);
        }, 2000);
        formData = {
          ...formData,
          price: [`${min[0]} TO ${max[0]}`],
        };
      } else {
        setTimeout(() => {
          setIsActiveClearPriceFilter(false);
        }, 2000);
      }
    } else {
      delete formData['price_from'];
      delete formData['price_to'];
    }
    setClearPageIndex(true);

    setFilterData(JSON.stringify(formData));
    setClearPriceFilter(false);
  };
  const onChangeSubmit = () => {
    const formData = new FormData(formRef.current as HTMLFormElement);

    const keys = Array.from(new Set(formData.keys()));

    let data: any = {};
    for (const key of keys) {
      data[key] = Array.from(formData.getAll(key));
    }
    onSubmit(data);
  };

  const clearFilter = () => {
    if (!pathname) return;
    // push the same pathname without query parameters
    router.push(pathname);
    setTimeout(() => {
      openFilterPanel(false);
    }, 200);
  };

  const onExpressSwitchChange = () => {
    if (!myLocation.value && !isExpressEnabled) {
      setShowLocationModal(true);
      setIsBopis(false);
      setIsMyLocation(true);
    }
    if (myLocation.value) {
      if (isExpressEnabled) {
        setIsExpressEnabled(false);
      } else {
        setIsExpressEnabled(true);
      }
    }
  };

  const onStoreSwitchChange = () => {
    if (!isStoreSelected) {
      setShowLocationModal(true);
      setIsBopis(true);
      setIsMyLocation(false);
    }

    if (selectedStore.Name) {
      if (isStoreSelected) {
        setIsStoreSelected(false);
        setSelectStore({} as IStoreLists);
      } else {
        setIsStoreSelected(true);
      }
    }
  };

  useEffect(() => {
    setIsStoreSelected(Boolean(selectedStore?.Code));
  }, [selectedStore]);

  return (
    <div className={getContainerClassName(asContent, 'flex grow flex-col overflow-hidden', 'px-7')}>
      {showPickInStoreOption && (
        <LocationSelector
          title="Pick from store"
          text={selectedStore.Name || 'Select Store'}
          onClick={() => {
            setIsMyLocation(false);
            setShowLocationModal(true);
            setIsBopis(true);
          }}
          isSelected={isStoreSelected}
          onSwitchChange={onStoreSwitchChange}
          hasValue={true}
        />
      )}

      {showExpressOption && (
        <LocationSelector
          title="Express Delivery"
          text={myLocation.label || 'Select Location'}
          onClick={() => {
            setIsMyLocation(true);
            setShowLocationModal(true);
            setIsBopis(false);
          }}
          onSwitchChange={onExpressSwitchChange}
          isSelected={isExpressEnabled}
          hasValue={true}
        />
      )}

      <BasicForm
        ref={formRef}
        onSubmit={onSubmit}
        className={getContainerClassName(asContent, 'filter-form relative flex grow flex-col overflow-hidden border-t', 'pb-24')}
      >
        <div className="overflow-y-auto last:border-b lg:overflow-y-clip [&>*:first-child]:border-t-0 [&>*]:border-t">
          {!asContent && (
            <section className={`open mb-6 border-t pt-5 ${className} `}>
              <button
                type="button"
                className={getContainerClassName(
                  asContent,
                  'a mb-5 flex w-full cursor-pointer justify-between border-none bg-transparent p-0 text-[15px] font-medium hover:opacity-70 focus:outline-2 focus:outline-offset-2 focus:outline-blue-500',
                  'md:pl-[19px]',
                )}
                onClick={() => setSortByOpen(!sortByOpen)}
                aria-expanded={sortByOpen}
                aria-label={sortByOpen ? 'Close sort options' : 'Open sort options'}
              >
                Sort by
                <IconArrowDown className={`mr-1 w-5 transition duration-300 ${sortByOpen ? '' : 'rotate-180'}`} aria-hidden="true" />
              </button>
              <div className={`options flex text-black ${sortByOpen ? '' : 'hidden'}`}>
                <SortBySection selectedSort={selectedSort} setSelectedSort={setSelectedSort} />
              </div>
            </section>
          )}
          {!giftVoucher && (
            <>
              <FilterSection
                asContent={asContent}
                filter={styleFilter}
                setShowMore={setShowMoreStyle}
                showMore={showMoreStyle}
                totalItems={styleFilter.options?.length}
                lessItems={lessStyleFilter.options?.length}
                optionsWrapperClassName={`${showMoreStyle ? 'max-h-[15.5rem]' : 'max-h-[14rem]'} grid-cols-2 place-items-center`}
                render={(option: any, index: number) => (
                  <StyleCheckbox
                    key={option.ReferenceId}
                    name="styles"
                    value={option.ReferenceId}
                    url={styleImage[index]}
                    label={option.Value}
                    selectedValues={selectedStyle}
                    className={`w-full p-2 ${index > lessStyleFilter.options?.length - 1 && !showMoreStyle ? 'hidden!' : ''}`}
                    onChange={() => asContent && onChangeSubmit()}
                    svgContentFromServer={styleSVGSourceCode?.[index]}
                  />
                )}
              />
              <FilterSection
                asContent={asContent}
                filter={sizeFilter}
                setShowMore={setShowMoreSize}
                showMore={showMoreSize}
                totalItems={sizeFilter.options?.length}
                lessItems={lessSizeFilter.options?.length}
                optionsWrapperClassName={`${showMoreSize ? 'max-h-[13.5rem]' : 'max-h-[9.8rem]'} grid-cols-4 md:grid-cols-3 lg:grid-cols-4 place-items-center`}
                render={(option: any, index: number) => (
                  <SizeCheckbox
                    key={option.Value}
                    name="sizes"
                    value={option.Value}
                    label={option.Value}
                    selectedValues={selectedSize}
                    className={`w-full p-2 ${index > lessSizeFilter.options?.length - 1 && !showMoreSize ? 'hidden!' : ''}`}
                    onChange={() => asContent && onChangeSubmit()}
                  />
                )}
              />

              <FilterSection
                asContent={asContent}
                filter={colorFilter}
                setShowMore={setShowMoreColor}
                showMore={showMoreColor}
                totalItems={colorFilter.options?.length}
                lessItems={lessColorFilter.options?.length}
                optionsWrapperClassName={`${showMoreColor ? 'max-h-[16rem]' : 'max-h-[14rem]'} grid-cols-2 place-items-center`}
                render={(option: any, index: number) => (
                  <ColorCheckbox
                    key={option.ReferenceId}
                    name="productColor"
                    colorName={option.Value}
                    value={option.ReferenceId}
                    label={colorCode[index]}
                    className={`-ml-4 w-full min-w-[50px] p-2 ${index > lessColorFilter.options?.length - 1 && !showMoreColor ? 'hidden!' : ''}`}
                    selectedValues={selectedColor}
                    onChange={() => asContent && onChangeSubmit()}
                  />
                )}
              />
              {pageType !== 'brand' && (
                <FilterSection
                  asContent={asContent}
                  filter={brandFilter}
                  setShowMore={setShowMoreBrand}
                  showMore={showMoreBrand}
                  totalItems={brandFilter.options?.length}
                  lessItems={lessBrandFilter.options?.length}
                  optionsWrapperClassName={`${showMoreBrand ? 'max-h-[15.2rem]' : 'max-h-[11rem]'}`}
                  render={(option: any, index: number) => (
                    <BasicCheckbox
                      key={option.Value}
                      selectedValues={selectedBrand}
                      name="brandSeName"
                      value={option.ReferenceId}
                      label={`${option.Value}`}
                      className={`${index > lessBrandFilter.options?.length - 1 && !showMoreBrand ? 'hidden!' : ''}`}
                      onChange={() => asContent && onChangeSubmit()}
                    />
                  )}
                />
              )}
              {pageType !== 'category' && (
                <FilterSection
                  asContent={asContent}
                  filter={categoryFilter}
                  setShowMore={setShowMoreCategory}
                  showMore={showMoreCategory}
                  totalItems={categoryFilter?.options?.length}
                  lessItems={lessCategoryFilter.options?.length}
                  optionsWrapperClassName="max-h-60"
                  render={(option: any, index: number) => (
                    <BasicCheckbox
                      key={option.Value}
                      selectedValues={selectedCategory}
                      name="categories"
                      value={option.ReferenceId}
                      label={`${option.Value}`}
                      className={`${index > lessCategoryFilter.options?.length - 1 && !showMoreCategory ? 'hidden!' : ''}`}
                      onChange={() => asContent && onChangeSubmit()}
                    />
                  )}
                />
              )}
              <FilterSection
                asContent={asContent}
                filter={genderFilter}
                setShowMore={setShowMoreGender}
                showMore={showMoreGender}
                totalItems={genderFilter?.options?.length}
                lessItems={lessGenderFilter.options?.length}
                optionsWrapperClassName="max-h-60"
                render={(option: any, index: number) => (
                  <BasicCheckbox
                    key={option.Value}
                    name="gender"
                    selectedValues={selectedGender}
                    value={option.Value}
                    label={option.Value}
                    className={`${index > lessGenderFilter.options?.length - 1 && !showMoreGender ? 'hidden!' : ''}`}
                    onChange={() => asContent && onChangeSubmit()}
                  />
                )}
              />

              <section className={`open mb-6 pt-5 ${className} ${priceRangeOpen ? 'open' : ''}`}>
                <div
                  className={`a mb-5 flex justify-between text-[15px] font-medium ${asContent ? '' : 'pr-5'}`}
                  onClick={() => setPriceRangeOpen(!priceRangeOpen)}
                >
                  Shop by price
                  <IconArrowDown className={`mr-1 w-5 transition duration-300 ${priceRangeOpen ? '' : 'rotate-180'}`} />
                </div>
                <div className={`px-4 pb-2 pt-2.5 ${priceRangeOpen ? '' : 'hidden'}`}>
                  <RangeSlider
                    name="price"
                    thumbSize={20}
                    color="dark"
                    label={(value) => numberFormat(value)}
                    mt="xl"
                    min={priceRange.Min}
                    max={priceRange.Max}
                    styles={(theme) => ({
                      label: {
                        padding: '0 0.25rem',
                      },
                    })}
                    marks={[
                      { value: priceRange.Min, label: '' },
                      { value: priceRange.Max, label: '' },
                    ]}
                    step={10}
                    value={[Number.isNaN(+price.minPrice) ? 0 : +price.minPrice, Number.isNaN(+price.maxPrice) ? 0 : +price.maxPrice]}
                    onChange={(e) => {
                      setPrice({ minPrice: +e[0], maxPrice: +e[1] });
                    }}
                    onChangeEnd={(e) => {
                      if (asContent) {
                        onChangeSubmit();
                      }
                    }}
                  />
                  <div className="mt-1 flex justify-between px-1 text-lg text-gray-500">
                    <span>{numberFormat(priceRange.Min)}</span>
                    <span>{numberFormat(priceRange.Max)}</span>
                  </div>
                </div>
              </section>
            </>
          )}
        </div>
        {!asContent && (
          <div className="absolute bottom-0 z-10 h-24 w-full bg-white py-5">
            <button
              type="submit"
              className={`w-full bg-black text-white ${filterLoading ? 'loading disabled:opacity-75' : ''}`}
              disabled={filterLoading}
            >
              Apply
            </button>
          </div>
        )}
      </BasicForm>

      {showLocationModal && (
        <LocationModal
          showLocationModal={showLocationModal}
          setShowLocationModal={setShowLocationModal}
          isMyLocation={isMyLocation}
          isBopis={isBopis}
        />
      )}
      {searchParams?.get('filters') && (
        <div className="text-center max-md:pb-4 md:pt-4">
          <button
            type="button"
            className="a cursor-pointer border-none bg-transparent p-0 text-xl underline hover:opacity-70 focus:outline-2 focus:outline-offset-2 focus:outline-blue-500"
            onClick={clearFilter}
            aria-label="Clear all filters"
          >
            Clear Filter
          </button>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
